import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import {
  getGrossMarginPercentage,
  getGrossMarginPercentageDelta,
  getGrossProfitDeltaInMicros,
  getGrossProfitInMicros,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import { getLocalizedPackUnit } from '@/utils/item';
import {
  groupIdentityOf,
  groupKeyOf,
  PricingGroupBy,
  PricingGroupIdentity,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-grouping';
import { isValidNumber } from '@/utils/number';
import { PackUnit } from '@/enums';
import {
  PriceEditDisplayCell,
  toPriceEditDisplayMap,
} from '@sales-portal/blocs/utils/price-edit-display';
import { PricingCalculations } from '@/utils/pricing-calculations';
import {
  PricingEdit,
  PricingInvoiceHistoryEntry,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { PricingItemManagementTableRow } from '@/experiences/sales-portal/domain/pricing/screens/management/types';
import { toPricingInvoiceHistory } from '@sales-portal/domain/pricing/blocs/transformers/pricing-invoice-history';
import { useMemo } from 'react';
import { usePricingEditsStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import {
  VariantPackCostChangeDataFieldsFragment,
  VariantPackCostType,
  VariantPackDetailedCostChangeDataFieldsFragment,
} from '@/types';

export type VariantPackCostChangeFragment =
  | VariantPackCostChangeDataFieldsFragment
  | VariantPackDetailedCostChangeDataFieldsFragment;

export type GroupSource = 'ERP' | 'USER';

export interface CostChangeVariantPackGroup {
  displayName: string;
  source: GroupSource;
  uuid: string;
}

// Values the API had no number for. The row still carries 0 in the numeric
// field (so math and sorting stay total), but consumers must render `--`
// instead of a fabricated zero. Only the Bookmarked tab produces these.
export enum CostChangeValue {
  Cost = 'COST',
  CostDelta = 'COST_DELTA',
  GrossMargin = 'GROSS_MARGIN',
  GrossMarginDelta = 'GROSS_MARGIN_DELTA',
  GrossProfit = 'GROSS_PROFIT',
  Price = 'PRICE',
  PriceDelta = 'PRICE_DELTA',
}

export type MissingCostChangeValues = ReadonlySet<CostChangeValue>;

export const isMissingValue = (
  row: Pick<VariantPackCostChange, 'missingValues'>,
  value: CostChangeValue,
): boolean => Boolean(row.missingValues?.has(value));

// An edit needs a cost to price off and a previous price to measure its deltas
// against (`buildPricingEdit` returns null without both), and a row missing its
// price or margin has no previous price. Such rows render as plain values and
// are skipped by bulk pricing — they stay selectable so bookmark actions can
// still reach them.
export const hasEditablePricing = (
  row: Pick<VariantPackCostChange, 'missingValues'>,
): boolean =>
  !isMissingValue(row, CostChangeValue.Price) &&
  !isMissingValue(row, CostChangeValue.GrossMargin);

export interface VariantPackCostChange {
  costChangeGrossProfitImpactInMicros: number;
  costDeltaInMicros: number;
  costInMicros: number;
  editsGrossProfitImpactInMicros: number;
  grossMarginPercentage: number;
  grossMarginPercentageDelta: number;
  grossProfitDeltaInMicros: number;
  grossProfitImpactInMicros: number;
  grossProfitInMicros: number;
  groups: CostChangeVariantPackGroup[];
  invoiceHistory: PricingInvoiceHistoryEntry[];
  isLocked: boolean;
  missingValues?: MissingCostChangeValues;
  netGrossProfitImpactInMicros: number;
  priceDeltaInMicros: number;
  priceInMicros: number;
  quantity: number;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackId: string;
  variantPackName: string;
  variantPackUOM: string;
  variantPackUUID: string;
  weeklyPurchaseQuantities: WeeklyPurchaseQuantity[];
}

export interface WeeklyPurchaseQuantity {
  endDate: string;
  quantity: number;
  startDate: string;
}
export interface PeerRange {
  maxGrossProfitInPercentage: number | null;
  minGrossProfitInPercentage: number | null;
}

export interface PricingAccountRow extends VariantPackCostChange {
  peerRange: PeerRange | null;
  priceFloorEnabled: boolean;
  priceFloorMode: VariantPackCostType | null;
  restaurantAccountId: string;
  restaurantName: string;
  supplierDisplayInfo: Record<string, PriceEditDisplayCell>;
}

// A parent stands for a group of accounts that may span suppliers, and nothing
// navigates by supplier any more, so it carries none.
export interface PricingGroupRow
  extends Omit<VariantPackCostChange, 'supplierUUID'>,
    PricingGroupIdentity {
  subRows: PricingAccountRow[];
}

// An edit supplies real numbers for everything it touches, so those values are
// no longer missing.
const EDIT_SUPPLIED_VALUES = [
  CostChangeValue.GrossMargin,
  CostChangeValue.GrossMarginDelta,
  CostChangeValue.GrossProfit,
  CostChangeValue.Price,
  CostChangeValue.PriceDelta,
];

const withoutValues = (
  missingValues: MissingCostChangeValues | undefined,
  resolved: CostChangeValue[],
): MissingCostChangeValues | undefined => {
  if (!missingValues?.size) {
    return missingValues;
  }
  const next = new Set(missingValues);
  resolved.forEach((value) => next.delete(value));
  return next.size > 0 ? next : undefined;
};

export const applyEditToRow = <T extends VariantPackCostChange>(
  row: T,
  edit: PricingEdit,
): T => {
  const editsGrossProfitImpactInMicros = edit.grossProfitImpactInMicros;
  const netGrossProfitImpactInMicros =
    row.costChangeGrossProfitImpactInMicros + editsGrossProfitImpactInMicros;

  return {
    ...row,
    editsGrossProfitImpactInMicros: edit.grossProfitImpactInMicros,
    grossMarginPercentage: edit.grossMarginPercentage,
    grossMarginPercentageDelta: edit.grossMarginPercentageDelta,
    grossProfitDeltaInMicros: edit.grossProfitDeltaInMicros,
    grossProfitImpactInMicros:
      row.costChangeGrossProfitImpactInMicros + editsGrossProfitImpactInMicros,
    grossProfitInMicros: edit.grossProfitInMicros,
    missingValues: withoutValues(row.missingValues, EDIT_SUPPLIED_VALUES),
    netGrossProfitImpactInMicros,
    priceDeltaInMicros: edit.priceDeltaInMicros,
    priceInMicros: edit.priceInMicros,
  };
};

const numberOrZero = (value: number | null | undefined): number =>
  isValidNumber(value) ? value : 0;

const buildVariantPackCostChange = (
  item: VariantPackCostChangeFragment,
  resolved: {
    grossMarginPercentage: number;
    grossMarginPercentageDelta: number;
    grossProfitDeltaInMicros: number;
    grossProfitInMicros: number;
  },
): VariantPackCostChange => {
  const variantPack = item.variant_pack;
  const restaurant = item.restaurant;

  const invoiceHistory =
    'invoice_history' in item && item.invoice_history
      ? toPricingInvoiceHistory(item.invoice_history)
      : [];

  const groups: CostChangeVariantPackGroup[] = (item.groups ?? []).map((g) => ({
    displayName: g.display_name,
    source: g.source as GroupSource,
    uuid: g.uuid,
  }));

  const weeklyPurchaseQuantities = (
    ('weekly_purchase_quantities' in item && item.weekly_purchase_quantities) ||
    []
  ).map((w) => ({
    endDate: w.end_date,
    quantity: w.quantity,
    startDate: w.start_date,
  }));

  const quantity = numberOrZero(item.weekly_quantity);

  // No edits on a freshly transformed row, so net impact == cost-change impact.
  const costChangeGrossProfitImpactInMicros =
    resolved.grossProfitDeltaInMicros * quantity;

  return {
    costChangeGrossProfitImpactInMicros,
    costDeltaInMicros: item.cost_in_micros_delta ?? 0,
    costInMicros: item.cost_in_micros ?? 0,
    editsGrossProfitImpactInMicros: 0,
    grossMarginPercentage: resolved.grossMarginPercentage,
    grossMarginPercentageDelta: resolved.grossMarginPercentageDelta,
    grossProfitDeltaInMicros: resolved.grossProfitDeltaInMicros,
    grossProfitImpactInMicros: costChangeGrossProfitImpactInMicros,
    grossProfitInMicros: resolved.grossProfitInMicros,
    groups,
    invoiceHistory,
    isLocked: 'is_locked' in item && item.is_locked === true,
    netGrossProfitImpactInMicros: costChangeGrossProfitImpactInMicros,
    priceDeltaInMicros: item.price_in_micros_delta ?? 0,
    priceInMicros: item.price_in_micros ?? 0,
    quantity,
    restaurantUUID: restaurant?.uuid ?? '',
    supplierUUID: item.supplier_id,
    variantPackId: variantPack?.external_item_id ?? '',
    variantPackName: variantPack?.item?.display_name ?? '',
    variantPackUOM: variantPack?.pack?.unit
      ? getLocalizedPackUnit(variantPack.pack.unit as PackUnit)
      : '--',
    variantPackUUID: variantPack?.uuid ?? '',
    weeklyPurchaseQuantities,
  };
};

export const transformFragmentToCostChange = (
  item: VariantPackCostChangeFragment,
): VariantPackCostChange | null => {
  const variantPack = item.variant_pack;
  const restaurant = item.restaurant;
  if (!variantPack?.uuid || !restaurant?.uuid) {
    return null;
  }

  if (
    !isValidNumber(item.cost_in_micros_delta) ||
    !isValidNumber(item.cost_in_micros) ||
    !isValidNumber(item.price_in_micros_delta) ||
    !isValidNumber(item.price_in_micros)
  ) {
    return null;
  }

  const grossProfitDeltaInMicros = getGrossProfitDeltaInMicros(item);
  const grossMarginPercentage = getGrossMarginPercentage(item);
  const grossMarginPercentageDelta = getGrossMarginPercentageDelta(item);
  const grossProfitInMicros = getGrossProfitInMicros(item);

  if (
    !isValidNumber(grossMarginPercentage) ||
    !isValidNumber(grossMarginPercentageDelta) ||
    !isValidNumber(grossProfitDeltaInMicros) ||
    !isValidNumber(grossProfitInMicros)
  ) {
    return null;
  }

  return buildVariantPackCostChange(item, {
    grossMarginPercentage,
    grossMarginPercentageDelta,
    grossProfitDeltaInMicros,
    grossProfitInMicros,
  });
};

// Tolerant variant: an edited or bookmarked row must render even when the
// cost-change pipeline has no data for it (null cost/price/GM%), so every
// missing numeric collapses to 0 and is recorded in missingValues for the UI to
// render as `--`.
const transformFragmentToCostChangeAllowingPartialData = (
  item: VariantPackCostChangeFragment,
): VariantPackCostChange | null => {
  if (!item.variant_pack?.uuid || !item.restaurant?.uuid) {
    return null;
  }

  const grossMarginPercentage = getGrossMarginPercentage(item);

  // Cost comes straight from the read — the bookmarks resolver runs the same
  // builder as the cost-change and item-detail queries. It is still nullable:
  // bookmarks force-include their pairs, so a row is emitted even when there is
  // no cost update in the lookback window, and missingValues renders it as `--`.
  const costInMicros = isValidNumber(item.cost_in_micros)
    ? item.cost_in_micros
    : null;

  const resolved = {
    grossMarginPercentage,
    grossMarginPercentageDelta: getGrossMarginPercentageDelta(item),
    grossProfitDeltaInMicros: getGrossProfitDeltaInMicros(item),
    grossProfitInMicros: PricingCalculations.grossProfitAmountFromPrice(
      item.price_in_micros,
      costInMicros,
    ),
  };

  const missingValues = new Set<CostChangeValue>();
  const trackMissing = (
    value: CostChangeValue,
    resolvedValue: number | null | undefined,
  ) => {
    if (!isValidNumber(resolvedValue)) {
      missingValues.add(value);
    }
  };

  trackMissing(CostChangeValue.Cost, costInMicros);
  trackMissing(CostChangeValue.CostDelta, item.cost_in_micros_delta);
  trackMissing(CostChangeValue.Price, item.price_in_micros);
  trackMissing(CostChangeValue.PriceDelta, item.price_in_micros_delta);
  trackMissing(CostChangeValue.GrossMargin, resolved.grossMarginPercentage);
  trackMissing(
    CostChangeValue.GrossMarginDelta,
    resolved.grossMarginPercentageDelta,
  );
  trackMissing(CostChangeValue.GrossProfit, resolved.grossProfitInMicros);

  return {
    ...buildVariantPackCostChange(item, {
      grossMarginPercentage: numberOrZero(resolved.grossMarginPercentage),
      grossMarginPercentageDelta: numberOrZero(
        resolved.grossMarginPercentageDelta,
      ),
      grossProfitDeltaInMicros: numberOrZero(resolved.grossProfitDeltaInMicros),
      grossProfitInMicros: numberOrZero(resolved.grossProfitInMicros),
    }),
    costInMicros: numberOrZero(costInMicros),
    missingValues: missingValues.size > 0 ? missingValues : undefined,
  };
};

const transformRow = (
  item: VariantPackCostChangeDataFieldsFragment,
): PricingItemManagementTableRow | null => transformFragmentToCostChange(item);

const WEIGHTED_FIELDS = [
  'priceInMicros',
  'priceDeltaInMicros',
  'costInMicros',
  'costDeltaInMicros',
  'grossProfitInMicros',
  'grossProfitDeltaInMicros',
  'grossMarginPercentage',
  'grossMarginPercentageDelta',
] as const;

// Missing numerics are stored as a placeholder 0, so a row lacking a field must
// drop out of that field's average entirely — counting its quantity in the
// denominator would drag the group toward zero. grossProfitDeltaInMicros has no
// missing marker, so it keeps the all-row total.
const WEIGHTED_FIELD_VALUES: Partial<
  Record<(typeof WEIGHTED_FIELDS)[number], CostChangeValue>
> = {
  costDeltaInMicros: CostChangeValue.CostDelta,
  costInMicros: CostChangeValue.Cost,
  grossMarginPercentage: CostChangeValue.GrossMargin,
  grossMarginPercentageDelta: CostChangeValue.GrossMarginDelta,
  grossProfitInMicros: CostChangeValue.GrossProfit,
  priceDeltaInMicros: CostChangeValue.PriceDelta,
  priceInMicros: CostChangeValue.Price,
};

// Volume behind a field: the quantity of rows that actually carry it.
const contributingQuantity = (
  rows: Pick<VariantPackCostChange, 'missingValues' | 'quantity'>[],
  value: CostChangeValue | undefined,
): number =>
  rows.reduce(
    (sum, row) =>
      value && isMissingValue(row, value) ? sum : sum + (row.quantity || 0),
    0,
  );

const aggregateWeeklyPurchaseQuantities = (
  rows: VariantPackCostChange[],
  template: WeeklyPurchaseQuantity[],
): WeeklyPurchaseQuantity[] =>
  template.map((week, i) => ({
    endDate: week.endDate,
    quantity: rows.reduce(
      (sum, r) => sum + (r.weeklyPurchaseQuantities?.[i]?.quantity ?? 0),
      0,
    ),
    startDate: week.startDate,
  }));

/** Buckets rows by key, keeping first-seen order for keys and rows alike. */
export const groupRowsBy = <T>(
  rows: readonly T[],
  keyOf: (row: T) => string,
): T[][] => {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const key = keyOf(row);
    const group = groups.get(key);
    if (group) {
      group.push(row);
    } else {
      groups.set(key, [row]);
    }
  }
  return [...groups.values()];
};

const MARGIN_FIELD_VALUES = {
  grossMarginPercentage: CostChangeValue.GrossMargin,
  grossMarginPercentageDelta: CostChangeValue.GrossMarginDelta,
} as const;

const revenueWeightedAverage = (
  group: VariantPackCostChange[],
  field: keyof typeof MARGIN_FIELD_VALUES,
): number | null => {
  let weightedSum = 0;
  let weightSum = 0;
  for (const item of group) {
    if (
      isMissingValue(item, CostChangeValue.Price) ||
      isMissingValue(item, MARGIN_FIELD_VALUES[field])
    ) {
      continue;
    }
    const revenue = item.priceInMicros * item.quantity;
    if (revenue <= 0) {
      continue;
    }
    weightedSum += item[field] * revenue;
    weightSum += revenue;
  }
  return weightSum > 0 ? weightedSum / weightSum : null;
};

// Across packs the per-unit fields still volume-weight, but margin is a ratio
// of dollars, so it blends by revenue rather than by unit count.
const aggregateAcrossPacks = <T extends VariantPackCostChange>(
  group: T[],
  result: Partial<T>,
  totalQuantity: number,
): void => {
  aggregateWithinPack(group, result, totalQuantity);
  (
    Object.keys(MARGIN_FIELD_VALUES) as (keyof typeof MARGIN_FIELD_VALUES)[]
  ).forEach((field) => {
    const average = revenueWeightedAverage(group, field);
    if (average !== null) {
      result[field] = average;
    }
  });
};

const aggregateWithinPack = <T extends VariantPackCostChange>(
  group: T[],
  result: Partial<T>,
  totalQuantity: number,
): void => {
  const template = group.find(
    (item) => item.weeklyPurchaseQuantities?.length,
  )?.weeklyPurchaseQuantities;
  result.weeklyPurchaseQuantities = template
    ? aggregateWeeklyPurchaseQuantities(group, template)
    : [];

  if (totalQuantity <= 0) {
    return;
  }
  WEIGHTED_FIELDS.forEach((field) => {
    const missingValue = WEIGHTED_FIELD_VALUES[field];
    const fieldQuantity = contributingQuantity(group, missingValue);
    // No row carries this field with volume — leave the 0 placeholder for
    // aggregateMissingValues to flag as `--`.
    if (fieldQuantity <= 0) {
      return;
    }

    const sumProduct = group.reduce((total, item) => {
      if (missingValue && isMissingValue(item, missingValue)) {
        return total;
      }
      return total + (item[field] ?? 0) * item.quantity;
    }, 0);

    result[field] = sumProduct / fieldQuantity;
  });
};

export interface AggregateOptions {
  /**
   * Leave rows whose cost did not move out of the averages and the volume.
   * Impacts still sum every row, since an edit on such a row is still real.
   */
  ignoreZeroCostDelta?: boolean;
}

/**
 * Collapses one group of rows into a single aggregate row. Impacts are summed,
 * per-unit fields are volume-weighted averages, and a group spanning packs
 * blends its margin by revenue instead. Identity fields come from the first
 * row, so a caller grouping by restaurant overwrites them.
 */
export const aggregatePricingRows = <T extends VariantPackCostChange>(
  group: T[],
  groupBy: PricingGroupBy = PricingGroupBy.VariantPack,
  { ignoreZeroCostDelta = false }: AggregateOptions = {},
): T => {
  const firstItem = group[0];
  const weighted = ignoreZeroCostDelta
    ? group.filter((item) => item.costDeltaInMicros !== 0)
    : group;

  const totalQuantity = weighted.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0,
  );

  const aggregatedResult: Partial<T> = {
    groups: firstItem.groups,
    isLocked: false,
    quantity: totalQuantity,
    restaurantUUID: firstItem.restaurantUUID,
    supplierUUID: firstItem.supplierUUID,
    variantPackId: firstItem.variantPackId,
    variantPackName: firstItem.variantPackName,
    variantPackUOM: firstItem.variantPackUOM,
    variantPackUUID: firstItem.variantPackUUID,
  } as Partial<T>;

  // Aggregate rows are never a price-floor source; set explicit defaults so
  // the account row subtype's required fields are never silently undefined.
  const aggregatedAccount = aggregatedResult as Partial<PricingAccountRow>;
  aggregatedAccount.priceFloorEnabled = false;
  aggregatedAccount.priceFloorMode = null;

  WEIGHTED_FIELDS.forEach((field) => {
    aggregatedResult[field] = 0;
  });

  // Sum each row's own impact so the aggregate matches the per-row and summary totals.
  aggregatedResult.costChangeGrossProfitImpactInMicros = group.reduce<number>(
    (total, item) => total + (item.costChangeGrossProfitImpactInMicros ?? 0),
    0,
  );
  aggregatedResult.editsGrossProfitImpactInMicros = group.reduce<number>(
    (total, item) => total + (item.editsGrossProfitImpactInMicros ?? 0),
    0,
  );
  aggregatedResult.grossProfitImpactInMicros = group.reduce<number>(
    (total, item) => total + (item.grossProfitImpactInMicros ?? 0),
    0,
  );
  aggregatedResult.netGrossProfitImpactInMicros =
    aggregatedResult.grossProfitImpactInMicros;

  if (groupBy === PricingGroupBy.Restaurant) {
    aggregateAcrossPacks(weighted, aggregatedResult, totalQuantity);
  } else {
    aggregateWithinPack(weighted, aggregatedResult, totalQuantity);
  }

  // Nothing fed the averages, so there is no number to show for them.
  if (weighted.length === 0) {
    aggregatedResult.missingValues = new Set(Object.values(CostChangeValue));
  }

  return aggregatedResult as T;
};

/** One weighted-average row per variant pack. */
export const calculateWeightedAverages = <T extends VariantPackCostChange>(
  data: T[],
  options: AggregateOptions = {},
): T[] =>
  groupRowsBy(data ?? [], (row) => row.variantPackUUID).map((group) =>
    aggregatePricingRows(group, PricingGroupBy.VariantPack, options),
  );

export enum CostChangeTransformMode {
  // Every row survives; missing numerics collapse to 0 and are recorded in
  // missingValues. For tables that must show what the user edited or
  // bookmarked, whatever the cost-change pipeline knows about it.
  PartialData = 'PARTIAL_DATA',
  // Rows are dropped unless the cost-change baseline is complete.
  Strict = 'STRICT',
}

const transformFragmentByMode = (
  item: VariantPackCostChangeFragment,
  mode: CostChangeTransformMode,
): VariantPackCostChange | null => {
  switch (mode) {
    case CostChangeTransformMode.PartialData:
      return transformFragmentToCostChangeAllowingPartialData(item);
    case CostChangeTransformMode.Strict:
      return transformFragmentToCostChange(item);
  }
};

export const usePricingAccountRowsTransformer = (
  data: VariantPackDetailedCostChangeDataFieldsFragment[],
  mode: CostChangeTransformMode = CostChangeTransformMode.Strict,
): PricingAccountRow[] => {
  return useMemo(
    () =>
      data.reduce<PricingAccountRow[]>((acc, item) => {
        const base = transformFragmentByMode(item, mode);
        if (!base || !item.restaurant) {
          return acc;
        }
        const peerGroup =
          'pricing_peer_group' in item ? item.pricing_peer_group : null;
        acc.push({
          ...base,
          peerRange: peerGroup
            ? {
                maxGrossProfitInPercentage:
                  peerGroup.max_gross_profit_in_percentage ?? null,
                minGrossProfitInPercentage:
                  peerGroup.min_gross_profit_in_percentage ?? null,
              }
            : null,
          priceFloorEnabled: item.price_floor_enabled,
          priceFloorMode: item.price_floor_mode ?? null,
          restaurantAccountId: item.restaurant_account_id ?? '',
          restaurantName: item.restaurant.name ?? '',
          restaurantUUID: item.restaurant.uuid,
          supplierDisplayInfo: toPriceEditDisplayMap(
            item.supplier_display_info,
          ),
        });
        return acc;
      }, []),
    [data, mode],
  );
};

export const useVariantPackCostChangeAggregateTransformer = (
  mergedData: PricingAccountRow[],
  { ignoreZeroCostDelta = false }: AggregateOptions = {},
): VariantPackCostChange[] => {
  return useMemo(
    () => calculateWeightedAverages(mergedData, { ignoreZeroCostDelta }),
    [ignoreZeroCostDelta, mergedData],
  );
};

export const useVariantPackCostChangeRowTransformer = (
  data: VariantPackCostChangeDataFieldsFragment[],
): VariantPackCostChange[] => {
  return useMemo(() => data.map(transformRow).filter((row) => !!row), [data]);
};

export const useVariantPackCostChangeDataTransformer = (
  data: VariantPackCostChangeDataFieldsFragment[],
): VariantPackCostChange[] => {
  const rows = useVariantPackCostChangeRowTransformer(data);
  return useMemo(() => calculateWeightedAverages(rows), [rows]);
};

// A variant-pack aggregate only has a number to show when at least one of its
// rows carries that field with volume behind it. Weighted averages also need
// volume overall: with zero total quantity every weighted field collapses to 0,
// which is an artifact rather than a value.
const aggregateMissingValues = (
  rows: VariantPackCostChange[],
  totalQuantity: number,
): MissingCostChangeValues | undefined => {
  if (totalQuantity <= 0) {
    return new Set(Object.values(CostChangeValue));
  }

  const missingValues = new Set(
    Object.values(CostChangeValue).filter(
      (value) => contributingQuantity(rows, value) <= 0,
    ),
  );
  return missingValues.size > 0 ? missingValues : undefined;
};

interface GroupedRowsOptions {
  /** Keep only the rows carrying a pending edit. */
  editedRowsOnly: boolean;
  groupBy: PricingGroupBy;
}

// A parent row stands for one grouping entity, so the other dimension's fields
// are blanked rather than left holding whatever the first child had.
const withGroupIdentity = (
  aggregate: VariantPackCostChange,
  identity: PricingGroupIdentity,
): Omit<VariantPackCostChange, 'supplierUUID'> & PricingGroupIdentity => {
  const parent: Partial<Pick<VariantPackCostChange, 'supplierUUID'>> &
    Omit<VariantPackCostChange, 'supplierUUID'> = { ...aggregate };
  delete parent.supplierUUID;
  return identity.groupBy === PricingGroupBy.Restaurant
    ? {
        ...parent,
        ...identity,
        groups: [],
        restaurantUUID: identity.groupKey,
        variantPackId: '',
        variantPackName: '',
        variantPackUOM: '',
        variantPackUUID: '',
      }
    : { ...parent, ...identity, restaurantUUID: '' };
};

const unionMissingValues = (
  ...sets: (MissingCostChangeValues | undefined)[]
): MissingCostChangeValues | undefined => {
  const union = new Set<CostChangeValue>();
  sets.forEach((set) => set?.forEach((value) => union.add(value)));
  return union.size > 0 ? union : undefined;
};

/**
 * Groups account rows by variant pack or by restaurant into a weighted-average
 * parent row whose subRows are the original (unedited) rows. Pending edits
 * overlay the rows the parent averages, so an in-progress change is reflected
 * in the totals.
 */
const useGroupedPricingRowsTransformer = (
  data: VariantPackDetailedCostChangeDataFieldsFragment[],
  { editedRowsOnly, groupBy }: GroupedRowsOptions,
): PricingGroupRow[] => {
  const pricingEdits = usePricingEditsStore((state) => state.edits);
  const accountRows = usePricingAccountRowsTransformer(
    data,
    CostChangeTransformMode.PartialData,
  );

  return useMemo(() => {
    const editsMap = new Map<string, PricingEdit>();
    pricingEdits.forEach((edit) => {
      editsMap.set(buildAccountKey(edit), edit);
    });

    const groupedData = accountRows.reduce<
      Record<
        string,
        {
          original: PricingAccountRow[];
          updated: PricingAccountRow[];
        }
      >
    >((acc, row) => {
      const edit = editsMap.get(buildAccountKey(row));

      if (editedRowsOnly && !edit) {
        return acc;
      }

      const key = groupKeyOf(row, groupBy);

      if (!acc[key]) {
        acc[key] = { original: [], updated: [] };
      }

      acc[key].original.push(row);
      acc[key].updated.push(edit ? applyEditToRow(row, edit) : row);
      return acc;
    }, {});

    return Object.values(groupedData).map(({ updated, original }) => {
      const aggregate = aggregatePricingRows(updated, groupBy);
      return {
        ...withGroupIdentity(aggregate, groupIdentityOf(original[0], groupBy)),
        missingValues: unionMissingValues(
          aggregate.missingValues,
          aggregateMissingValues(updated, aggregate.quantity),
        ),
        subRows: original,
      };
    });
  }, [accountRows, editedRowsOnly, groupBy, pricingEdits]);
};

// The Review tab only shows what is about to be submitted: rows with an edit.
export const usePricingReviewRowsTransformer = (
  data: VariantPackDetailedCostChangeDataFieldsFragment[],
  groupBy: PricingGroupBy = PricingGroupBy.VariantPack,
): PricingGroupRow[] =>
  useGroupedPricingRowsTransformer(data, { editedRowsOnly: true, groupBy });

// The Bookmarked tab shows every bookmarked row, edited or not.
export const usePricingBookmarkRowsTransformer = (
  data: VariantPackDetailedCostChangeDataFieldsFragment[],
  groupBy: PricingGroupBy = PricingGroupBy.VariantPack,
): PricingGroupRow[] =>
  useGroupedPricingRowsTransformer(data, { editedRowsOnly: false, groupBy });
